import axios from 'axios';
import { store } from '../../store/store';
import { logout, updateAccessToken } from '../../store/authSlice';
import { getPersistedAccessToken, readPersistedSession, writePersistedSession } from '../../shared/authSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.host}/api/v1`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.accessToken || getPersistedAccessToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't handle 401 for auth endpoints - these are expected to return 401 for invalid credentials
    // Also exclude tenant info endpoint as it's public info needed before login
    const authEndpoints = ['/auth/login', '/auth/login/sso', '/auth/forgot-password', '/auth/reset-password', '/auth/tenant'];
    const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      const state = store.getState();
      const refreshToken = state.auth.refreshToken;
      
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });
          
          const newAccessToken = response.data.data.accessToken;
          store.dispatch(updateAccessToken(newAccessToken));
          
          const session = readPersistedSession();
          writePersistedSession({
            user: session?.user || state.auth.user || null,
            accessToken: newAccessToken,
            refreshToken: session?.refreshToken || refreshToken || null
          });
          
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          store.dispatch(logout());
          window.location.href = '/login';
        }
      } else {
        store.dispatch(logout());
        window.location.href = '/login';
      }
    }

    const apiMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.response?.data?.data?.error ||
      error.response?.data?.data?.message;

    if (apiMessage) {
      error.message = apiMessage;
    }
    
    return Promise.reject(error);
  }
);

export default api;
